/**
 * Cart Module
 * Handles all cart operations
 */
export class Cart {
    constructor() {
        this.cart = null;
        this.cartCountEl = document.querySelector('[data-cart-count]');
        this.cartTotalEl = document.querySelector('[data-cart-total]');
        this.miniCartEl = document.querySelector('[data-mini-cart]');
        this.bindEvents();
        this.refresh();
    }
    bindEvents() {
        // Add to cart forms
        document.addEventListener('submit', (e) => {
            const form = e.target;
            if (form.matches('[data-add-to-cart-form]')) {
                e.preventDefault();
                this.handleAddToCartForm(form);
            }
        });
        // Quick add buttons
        document.addEventListener('click', (e) => {
            const button = e.target.closest('[data-add-to-cart-quick]');
            if (button) {
                e.preventDefault();
                const productId = parseInt(button.getAttribute('data-product-id') || '0');
                if (productId) {
                    this.addItem(productId, 1);
                }
            }
        });
        // Update quantity
        document.addEventListener('change', (e) => {
            const input = e.target;
            if (input.matches('[data-cart-item-quantity]')) {
                const itemId = parseInt(input.getAttribute('data-item-id') || '0');
                const quantity = parseInt(input.value);
                if (itemId) {
                    this.updateItem(itemId, quantity);
                }
            }
        });
        // Remove item
        document.addEventListener('click', (e) => {
            const button = e.target.closest('[data-cart-item-remove]');
            if (button) {
                e.preventDefault();
                const itemId = parseInt(button.getAttribute('data-item-id') || '0');
                if (itemId) {
                    this.removeItem(itemId);
                }
            }
        });
        // Quantity decrease button
        document.addEventListener('click', (e) => {
            const button = e.target.closest('[data-quantity-decrease]');
            if (button) {
                e.preventDefault();
                const itemId = parseInt(button.getAttribute('data-item-id') || '0');
                const input = document.querySelector(`[data-cart-item-quantity][data-item-id="${itemId}"]`);
                if (itemId && input) {
                    const newQty = Math.max(1, parseInt(input.value) - 1);
                    input.value = newQty.toString();
                    this.updateItem(itemId, newQty);
                }
            }
        });
        // Quantity increase button
        document.addEventListener('click', (e) => {
            const button = e.target.closest('[data-quantity-increase]');
            if (button) {
                e.preventDefault();
                const itemId = parseInt(button.getAttribute('data-item-id') || '0');
                const input = document.querySelector(`[data-cart-item-quantity][data-item-id="${itemId}"]`);
                if (itemId && input) {
                    const newQty = parseInt(input.value) + 1;
                    input.value = newQty.toString();
                    this.updateItem(itemId, newQty);
                }
            }
        });
        // Update Cart button (refreshes all quantities)
        document.addEventListener('click', (e) => {
            const button = e.target.closest('[data-update-cart]');
            if (button) {
                e.preventDefault();
                this.refresh().then(() => {
                    this.showNotification('Cart updated', 'success');
                });
            }
        });
        // Apply coupon form
        document.addEventListener('submit', (e) => {
            const form = e.target;
            if (form.matches('[data-coupon-form]')) {
                e.preventDefault();
                const input = form.querySelector('input[name="couponCode"]');
                const code = input?.value?.trim();
                if (code) {
                    // Show loading state on button
                    const btnText = form.querySelector('[data-coupon-btn-text]');
                    const btnLoading = form.querySelector('[data-coupon-btn-loading]');
                    const submitBtn = form.querySelector('[data-coupon-submit]');
                    if (btnText)
                        btnText.style.display = 'none';
                    if (btnLoading)
                        btnLoading.style.display = 'inline';
                    if (submitBtn)
                        submitBtn.disabled = true;
                    this.applyCoupon(code).then((success) => {
                        if (success) {
                            this.showCouponApplied(code);
                        }
                        else {
                            // Reset button state on failure
                            if (btnText)
                                btnText.style.display = '';
                            if (btnLoading)
                                btnLoading.style.display = 'none';
                            if (submitBtn)
                                submitBtn.disabled = false;
                        }
                    });
                }
                else {
                    this.showCouponMessage('Please enter a coupon code', 'error');
                }
            }
        });
        // Remove coupon buttons
        document.addEventListener('click', (e) => {
            const button = e.target.closest('[data-remove-coupon], [data-remove-coupon-btn]');
            if (button) {
                e.preventDefault();
                this.removeCoupon().then(() => {
                    this.hideCouponApplied();
                });
            }
        });
    }
    async handleAddToCartForm(form) {
        const formData = new FormData(form);
        const productId = parseInt(formData.get('product_id'));
        const quantity = parseInt(formData.get('quantity')) || 1;
        const variantId = formData.get('variant_id')
            ? parseInt(formData.get('variant_id'))
            : undefined;
        // Get the submit button and set loading state
        const submitBtn = form.querySelector('[data-add-to-cart]');
        if (submitBtn) {
            submitBtn.classList.add('is-loading');
            submitBtn.disabled = true;
        }
        // Collect options
        const options = {};
        formData.forEach((value, key) => {
            if (key.startsWith('option[')) {
                const optionId = key.match(/option\[(\d+)\]/)?.[1];
                if (optionId) {
                    options[optionId] = value;
                }
            }
        });
        // Get design data if present
        const designDataEl = form.querySelector('[data-design-data]');
        const designData = designDataEl?.getAttribute('data-design-data')
            ? JSON.parse(designDataEl.getAttribute('data-design-data'))
            : undefined;
        try {
            await this.addItem(productId, quantity, variantId, Object.keys(options).length > 0 ? options : undefined, designData);
        }
        finally {
            // Reset button state
            if (submitBtn) {
                submitBtn.classList.remove('is-loading');
                submitBtn.disabled = false;
            }
        }
    }
    async addItem(productId, quantity = 1, variantId, options, designData) {
        try {
            this.showLoading();
            const response = await fetch('/api/cart/items', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({
                    productId,
                    quantity,
                    variantId,
                    options,
                    designData,
                }),
            });
            const data = await response.json();
            if (data.success) {
                this.cart = data.data;
                this.updateUI();
                this.showMiniCart();
                this.emit('cart:item-added', { productId, quantity });
                this.showNotification('Item added to cart', 'success');
            }
            else {
                throw new Error(data.error || 'Failed to add item');
            }
        }
        catch (error) {
            console.error('Add to cart error:', error);
            this.showNotification('Failed to add item to cart', 'error');
            this.emit('cart:error', { error });
        }
        finally {
            this.hideLoading();
        }
    }
    async updateItem(itemId, quantity) {
        try {
            this.showLoading();
            const response = await fetch(`/api/cart/items/${itemId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({ quantity }),
            });
            const data = await response.json();
            if (data.success) {
                this.cart = data.data;
                this.updateUI();
                this.emit('cart:updated', { itemId, quantity });
            }
            else {
                throw new Error(data.error || 'Failed to update item');
            }
        }
        catch (error) {
            console.error('Update cart error:', error);
            this.showNotification('Failed to update cart', 'error');
            this.emit('cart:error', { error });
        }
        finally {
            this.hideLoading();
        }
    }
    async removeItem(itemId) {
        console.log('[CART DEBUG] removeItem called with itemId:', itemId);
        try {
            this.showLoading();
            console.log('[CART DEBUG] Sending DELETE request to /api/cart/items/' + itemId);
            const response = await fetch(`/api/cart/items/${itemId}`, {
                method: 'DELETE',
                credentials: 'include',
            });
            console.log('[CART DEBUG] Response status:', response.status);
            const data = await response.json();
            console.log('[CART DEBUG] Response data:', JSON.stringify(data, null, 2));
            if (data.success) {
                console.log('[CART DEBUG] Delete successful, updating cart state');
                this.cart = data.data;
                console.log('[CART DEBUG] New cart item count:', this.cart?.itemCount);
                this.updateUI();
                this.removeItemFromDOM(itemId);
                this.emit('cart:item-removed', { itemId });
                this.showNotification('Item removed from cart', 'success');
            }
            else {
                console.error('[CART DEBUG] Delete failed:', data.error);
                throw new Error(data.error || 'Failed to remove item');
            }
        }
        catch (error) {
            console.error('[CART DEBUG] Remove from cart error:', error);
            this.showNotification('Failed to remove item', 'error');
            this.emit('cart:error', { error });
        }
        finally {
            this.hideLoading();
        }
    }
    async applyCoupon(code) {
        try {
            this.showLoading();
            const response = await fetch('/api/cart/coupon', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({ couponCode: code }),
            });
            const data = await response.json();
            if (data.success) {
                this.cart = data.data;
                this.updateUI();
                this.showNotification('Coupon applied successfully!', 'success');
                return true;
            }
            else {
                this.showCouponMessage(data.error || 'Invalid coupon code', 'error');
                return false;
            }
        }
        catch (error) {
            console.error('Apply coupon error:', error);
            this.showCouponMessage('Failed to apply coupon. Please try again.', 'error');
            return false;
        }
        finally {
            this.hideLoading();
        }
    }
    async removeCoupon() {
        try {
            this.showLoading();
            const response = await fetch('/api/cart/coupon', {
                method: 'DELETE',
                credentials: 'include',
            });
            const data = await response.json();
            if (data.success) {
                this.cart = data.data;
                this.updateUI();
                this.showNotification('Coupon removed', 'success');
            }
        }
        catch (error) {
            console.error('Remove coupon error:', error);
        }
        finally {
            this.hideLoading();
        }
    }
    async refresh() {
        try {
            const response = await fetch('/api/cart', {
                credentials: 'include',
            });
            const data = await response.json();
            if (data.success) {
                this.cart = data.data;
                this.updateUI();
            }
        }
        catch (error) {
            console.error('Refresh cart error:', error);
        }
    }
    updateUI() {
        if (!this.cart)
            return;
        // Update cart count
        if (this.cartCountEl) {
            this.cartCountEl.textContent = this.cart.itemCount.toString();
            this.cartCountEl.classList.toggle('has-items', this.cart.itemCount > 0);
        }
        // Update cart total
        if (this.cartTotalEl) {
            this.cartTotalEl.textContent = this.formatMoney(this.cart.grandTotal);
        }
        // Update mini cart
        this.updateMiniCart();
        // Update cart page if we're on it
        this.updateCartPage();
        // Dispatch event for other scripts
        this.emit('cart:updated', { cart: this.cart });
    }
    updateCartPage() {
        if (!this.cart)
            return;
        // Update subtotal
        const subtotalEl = document.querySelector('[data-cart-subtotal]');
        if (subtotalEl) {
            subtotalEl.textContent = this.formatMoney(this.cart.subtotal);
        }
        // Update discount
        const discountEl = document.querySelector('[data-cart-discount]');
        if (discountEl && this.cart.discountTotal) {
            discountEl.textContent = `-${this.formatMoney(this.cart.discountTotal)}`;
        }
        // Update total
        const totalEl = document.querySelector('[data-cart-total]');
        if (totalEl) {
            totalEl.textContent = this.formatMoney(this.cart.grandTotal);
        }
        // Update line item totals
        for (const item of this.cart.items) {
            const lineTotal = document.querySelector(`[data-cart-item][data-item-id="${item.id}"] [data-line-total]`);
            if (lineTotal) {
                lineTotal.textContent = this.formatMoney(item.total);
            }
            // Update quantity input value
            const qtyInput = document.querySelector(`[data-cart-item-quantity][data-item-id="${item.id}"]`);
            if (qtyInput && parseInt(qtyInput.value) !== item.quantity) {
                qtyInput.value = item.quantity.toString();
            }
        }
        // Update discount line visibility and amount
        const discountLine = document.querySelector('[data-discount-line]');
        if (discountLine) {
            const hasDiscount = this.cart.discountTotal && this.cart.discountTotal > 0;
            discountLine.style.display = hasDiscount ? '' : 'none';
            const discountEl = discountLine.querySelector('[data-cart-discount]');
            if (discountEl && hasDiscount) {
                discountEl.textContent = `-${this.formatMoney(this.cart.discountTotal)}`;
            }
            const couponCodeEl = discountLine.querySelector('[data-coupon-code]');
            if (couponCodeEl) {
                couponCodeEl.textContent = this.cart.couponCode ? `(${this.cart.couponCode})` : '';
            }
            const removeCouponBtn = discountLine.querySelector('[data-remove-coupon]');
            if (removeCouponBtn) {
                removeCouponBtn.style.display = this.cart.couponCode ? '' : 'none';
            }
        }
        // Update earnable points
        if (this.cart.earnablePoints !== undefined) {
            const earnablePointsEls = document.querySelectorAll('[data-earnable-points]');
            earnablePointsEls.forEach(el => {
                el.textContent = this.cart.earnablePoints.toString();
            });
        }
    }
    updateMiniCart() {
        if (!this.miniCartEl || !this.cart)
            return;
        const itemsContainer = this.miniCartEl.querySelector('[data-mini-cart-items]');
        const emptyMessage = this.miniCartEl.querySelector('[data-mini-cart-empty]');
        const footer = this.miniCartEl.querySelector('[data-mini-cart-footer]');
        if (this.cart.items.length === 0) {
            if (itemsContainer)
                itemsContainer.innerHTML = '';
            if (emptyMessage)
                emptyMessage.classList.remove('hidden');
            if (footer)
                footer.classList.add('hidden');
            return;
        }
        if (emptyMessage)
            emptyMessage.classList.add('hidden');
        if (footer)
            footer.classList.remove('hidden');
        if (itemsContainer) {
            itemsContainer.innerHTML = this.cart.items
                .map((item) => `
        <div class="mini-cart-item" data-item-id="${item.id}">
          <a href="${item.url}" class="mini-cart-item__image">
            <img src="${item.imageUrl || '/assets/images/placeholder.png'}" alt="${item.name}">
          </a>
          <div class="mini-cart-item__details">
            <a href="${item.url}" class="mini-cart-item__name">${item.name}</a>
            <div class="mini-cart-item__qty">Qty: ${item.quantity}</div>
            <div class="mini-cart-item__price">${this.formatMoney(item.total)}</div>
          </div>
          <button type="button" class="mini-cart-item__remove" data-cart-item-remove data-item-id="${item.id}">
            <span class="sr-only">Remove</span>
            &times;
          </button>
        </div>
      `)
                .join('');
        }
        // Update totals
        const subtotalEl = this.miniCartEl.querySelector('[data-mini-cart-subtotal]');
        if (subtotalEl) {
            subtotalEl.textContent = this.formatMoney(this.cart.subtotal);
        }
        // Update earnable points
        if (this.cart.earnablePoints !== undefined) {
            const earnablePointsEl = this.miniCartEl.querySelector('[data-earnable-points]');
            if (earnablePointsEl) {
                earnablePointsEl.textContent = this.cart.earnablePoints.toString();
            }
        }
    }
    removeItemFromDOM(itemId) {
        // Find by data-cart-item with data-item-id attribute (cart page)
        // Also check for mini-cart items
        const itemEl = document.querySelector(`[data-cart-item][data-item-id="${itemId}"]`) ||
            document.querySelector(`[data-item-id="${itemId}"].cart-row`) ||
            document.querySelector(`.mini-cart-item[data-item-id="${itemId}"]`);
        console.log('[CART DEBUG] removeItemFromDOM called for itemId:', itemId, 'found:', !!itemEl);
        if (itemEl) {
            itemEl.classList.add('removing');
            setTimeout(() => {
                itemEl.remove();
                console.log('[CART DEBUG] Item removed from DOM:', itemId);
                // Check if cart is now empty and show empty state
                const remainingItems = document.querySelectorAll('[data-cart-item][data-item-id]');
                console.log('[CART DEBUG] Remaining items:', remainingItems.length);
                if (remainingItems.length === 0) {
                    // Reload page to show empty cart state
                    window.location.reload();
                }
            }, 300);
        }
        else {
            console.log('[CART DEBUG] Item element not found in DOM, reloading page');
            // Fallback: reload page if we can't find the element
            window.location.reload();
        }
    }
    showMiniCart() {
        if (this.miniCartEl) {
            this.miniCartEl.classList.add('is-open');
            setTimeout(() => {
                this.miniCartEl?.classList.remove('is-open');
            }, 5000);
        }
    }
    showLoading() {
        document.body.classList.add('cart-loading');
    }
    hideLoading() {
        document.body.classList.remove('cart-loading');
    }
    showNotification(message, type = 'info') {
        const notification = document.createElement('div');
        notification.className = `notification notification--${type}`;
        notification.textContent = message;
        const container = document.querySelector('[data-notifications]') || document.body;
        container.appendChild(notification);
        setTimeout(() => {
            notification.classList.add('fade-out');
            setTimeout(() => notification.remove(), 300);
        }, 3000);
    }
    showCouponApplied(code) {
        const form = document.querySelector('[data-coupon-form]');
        const applied = document.querySelector('[data-coupon-applied]');
        const appliedCode = document.querySelector('[data-applied-code]');
        if (form)
            form.style.display = 'none';
        if (applied) {
            applied.style.display = '';
            if (appliedCode)
                appliedCode.textContent = code;
        }
    }
    hideCouponApplied() {
        const form = document.querySelector('[data-coupon-form]');
        const applied = document.querySelector('[data-coupon-applied]');
        const input = form?.querySelector('input[name="couponCode"]');
        const btnText = form?.querySelector('[data-coupon-btn-text]');
        const btnLoading = form?.querySelector('[data-coupon-btn-loading]');
        const submitBtn = form?.querySelector('[data-coupon-submit]');
        if (form)
            form.style.display = '';
        if (applied)
            applied.style.display = 'none';
        if (input)
            input.value = '';
        if (btnText)
            btnText.style.display = '';
        if (btnLoading)
            btnLoading.style.display = 'none';
        if (submitBtn)
            submitBtn.disabled = false;
    }
    showCouponMessage(message, type) {
        const messageEl = document.querySelector('[data-coupon-message]');
        if (messageEl) {
            messageEl.textContent = message;
            messageEl.className = `cart-coupon__message cart-coupon__message--${type}`;
            setTimeout(() => {
                messageEl.textContent = '';
                messageEl.className = 'cart-coupon__message';
            }, 4000);
        }
    }
    formatMoney(amount) {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
        }).format(amount);
    }
    emit(event, detail) {
        document.dispatchEvent(new CustomEvent(event, { detail }));
    }
    // Public getters
    getCart() {
        return this.cart;
    }
    getItemCount() {
        return this.cart?.itemCount || 0;
    }
    getTotal() {
        return this.cart?.grandTotal || 0;
    }
}
export default Cart;
